
import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { AppDialog } from "@/components/ui/app-dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useConnections } from '@/hooks/useConnections';
import { useRewardPledges } from '@/hooks/useRewardPledges';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfiles } from '@/hooks/profiles';  // Fixed import path
import { NotificationHeader } from './NotificationHeader';
import { NotificationList } from './NotificationList';
import { NotificationFooter } from './NotificationFooter';
import { Notification } from './NotificationItem';
import { logger } from '@/utils/logger';
import { useNavigate } from 'react-router-dom';

interface NotificationCenterProps {
  embedded?: boolean;
  autoOpen?: boolean;
  hideTrigger?: boolean;
  onRequestClose?: () => void;
}

export function NotificationCenter({ embedded = false, autoOpen = false, hideTrigger = false, onRequestClose }: NotificationCenterProps) {
  const [open, setOpen] = useState(autoOpen);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    loading, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification,
    fetchNotifications,
    getNotificationDescription,
    realtimeStatus,
    lastUpdateTime,
    reconnect
  } = useNotifications();
  const { acceptConnection, rejectConnection, fetchConnections } = useConnections();
  const { fetchPledges, fulfillPledge } = useRewardPledges();
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Extract user IDs from notifications to get profile info
  const userIds = React.useMemo(() => {
    const ids = new Set<string>();
    
    notifications.forEach(notification => {
      try {
        if (notification.type === 'connection_request') {
          let parsedContent: any;
          
          if (typeof notification.content === 'string') {
            parsedContent = JSON.parse(notification.content);
          } else if (notification.content && typeof notification.content === 'object') {
            parsedContent = notification.content;
          }
          
          if (parsedContent?.sender_id) {
            ids.add(parsedContent.sender_id);
          }
        } else if (notification.type === 'connection_response') {
          let parsedContent: any;
          
          if (typeof notification.content === 'string') {
            parsedContent = JSON.parse(notification.content);
          } else if (notification.content && typeof notification.content === 'object') {
            parsedContent = notification.content;
          }
          
          if (parsedContent?.receiver_id) {
            ids.add(parsedContent.receiver_id);
          }
        }
      } catch (e) {
        logger.error("Error parsing notification content", e);
      }
    });
    
    return Array.from(ids);
  }, [notifications]);
  
  // Get profiles for user IDs
  const { getDisplayName } = useProfiles(userIds);
  
  // eslint-disable-next-line -- intentionally empty, notifications fetched on mount
  
  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleAcceptConnection = async (connectionId: string, notificationId: string) => {
    const success = await acceptConnection(connectionId);
    if (success) {
      markAsRead(notificationId);
      fetchNotifications();
      fetchConnections();
      toast({
        title: "Connection accepted",
        description: "You are now connected with this user",
      });
    }
  };

  const handleRejectConnection = async (connectionId: string, notificationId: string) => {
    const success = await rejectConnection(connectionId);
    if (success) {
      deleteNotification(notificationId);
      fetchConnections();
      toast({
        title: "Connection rejected",
        description: "The connection request has been rejected",
      });
    }
  };

  const handleAcceptRewardPledge = async (pledgeId: string, notificationId: string) => {
    try {
      const { data: userResp } = await supabase.auth.getUser();
      const userId = userResp?.user?.id;
      if (!userId) throw new Error('Not authenticated');

      

      // Get pledge details first to determine best approach
      const { data: pledgeData } = await supabase
        .from('reward_pledges')
        .select('to_user_id, claim_token')
        .eq('id', pledgeId)
        .single();

      if (pledgeData?.to_user_id === userId) {
        const { error } = await supabase.rpc('accept_reward_pledge', {
          pledge_id_param: pledgeId
        });
        if (error) throw new Error(error.message || 'accept_reward_pledge failed');
      } else if (pledgeData?.claim_token) {
        // Fallback for phone gifts or mismatched to_user_id.
        // The server uses auth.uid() and ignores user_id_param (kept for backwards compatibility).
        const { error } = await supabase.rpc('claim_reward_pledge', {
          claim_token_param: pledgeData.claim_token,
          user_id_param: userId
        });
        if (error) throw new Error(error.message || 'claim_reward_pledge failed');
      } else {
        throw new Error('This reward is not assigned to you and has no claim token');
      }

      toast({
        title: "Deal accepted!",
        description: "You've accepted this reward pledge. The sender has been notified."
      });

      markAsRead(notificationId);
      fetchNotifications();
      fetchPledges(); // Refresh pledges data
    } catch (error: any) {
      logger.error('Error accepting reward pledge:', error);
      toast({
        title: "Failed to accept reward",
        description: error?.message || "Please try again or contact support.",
        variant: "destructive"
      });
    }
  };

  const handleFulfillPledge = async (pledgeId: string, notificationId: string) => {
    try {
      const success = await fulfillPledge(pledgeId);
      if (success) {
        toast({
          title: "Reward Fulfilled! 🎉",
          description: "The recipient has been notified about the fulfilled reward."
        });
        markAsRead(notificationId);
        fetchNotifications();
      }
    } catch (error: any) {
      logger.error('Error fulfilling pledge from notification:', error);
      toast({
        title: "Failed to fulfill reward",
        description: error?.message || "Please try again.",
        variant: "destructive"
      });
    }
  };

  // This effect forces a fetch of notifications when the component mounts
  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user, fetchNotifications]);

  useEffect(() => {
    if (!autoOpen) return;
    if (user) fetchNotifications();
    setOpen(true);
  }, [autoOpen, user, fetchNotifications]);

  // Removed redundant setup_notifications_realtime call - already handled in useNotifications hook

  const isMobile = useIsMobile();

  const closeSurface = () => {
    setOpen(false);
    onRequestClose?.();
  };

  const openCenter = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (user) fetchNotifications();
    setOpen(true);
  };

  const trigger = (
    <Button variant="ghost" size="icon" className="relative" onClick={openCenter} aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}>
      <Bell className={`h-5 w-5 ${unreadCount > 0 ? 'text-primary' : ''}`} />
      {unreadCount > 0 && (
        <Badge variant="destructive" className="absolute -top-1 -right-1 w-5 h-5 p-0 flex items-center justify-center text-[10px] shadow-lg">
          {unreadCount > 9 ? '9+' : unreadCount}
        </Badge>
      )}
      {realtimeStatus === 'error' && (
        <Badge variant="outline" className="absolute -bottom-1 -right-1 w-3 h-3 p-0 bg-red-500 border-red-500" />
      )}
    </Button>
  );

  // Close the center whenever the user takes an action that routes elsewhere
  // (opens another sheet, navigates to a page). Prevents overlay stacking.
  const closeAnd = <T extends (...args: any[]) => any>(fn?: T) =>
    ((...args: Parameters<T>) => {
      closeSurface();
      return fn?.(...args);
    }) as T;

  const handleNavigateFromNotification = (path: string) => {
    closeSurface();
    window.setTimeout(() => {
      navigate(path);
    }, 0);
  };

  const body = (
    <>
      <NotificationHeader
        realtimeStatus={realtimeStatus}
        unreadCount={unreadCount}
        handleMarkAllAsRead={handleMarkAllAsRead}
        reconnect={reconnect}
        toast={toast}
        filter={filter}
        onFilterChange={setFilter}
      />
      <Separator />
      <NotificationList
        loading={loading}
        notifications={notifications}
        markAsRead={markAsRead}
        deleteNotification={deleteNotification}
        handleAcceptConnection={closeAnd(handleAcceptConnection)}
        handleRejectConnection={closeAnd(handleRejectConnection)}
        handleAcceptRewardPledge={closeAnd(handleAcceptRewardPledge)}
        handleFulfillPledge={closeAnd(handleFulfillPledge)}
        getNotificationDescription={getNotificationDescription}
        filter={filter}
        onNavigate={handleNavigateFromNotification}
        scrollClassName={embedded ? "h-[calc(85vh-210px)]" : isMobile ? "h-[calc(100dvh-210px)] md:h-[340px]" : "h-[340px]"}
      />
      <NotificationFooter
        realtimeStatus={realtimeStatus}
        lastUpdateTime={lastUpdateTime}
        reconnect={reconnect}
        toast={toast}
      />
    </>
  );

  if (embedded) {
    return <div className="flex flex-col h-full">{body}</div>;
  }

  if (hideTrigger) {
    return (
      <AppDialog open={open} onClose={closeSurface} size="lg" ariaTitle="Notifications" className="md:max-w-lg">
        <div className="flex flex-col h-full pt-14 md:pt-0">{body}</div>
      </AppDialog>
    );
  }

  if (isMobile) {
    // Mobile: single AppDialog only — no Popover co-mounted (previously caused
    // the "half window under the sheet" ghosting bug on mobile).
    return (
      <>
        {!hideTrigger && trigger}
        <AppDialog open={open} onClose={closeSurface} size="lg" ariaTitle="Notifications" className="md:max-w-lg">
          <div className="flex flex-col h-full pt-14 md:pt-0">{body}</div>
        </AppDialog>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        {body}
      </PopoverContent>
    </Popover>
  );
}


